// Sentence objects from the challenges backend carry both the raw sentence
// (`value`) and a formatted one (`humanReadable`). Show humanReadable to the
// player; keep `value` for anything sent back to the API — the parser only
// accepts the raw form. Falls back to `value` for data that predates the
// field (presets.json, runs stored in Firestore).
export function displayValue(sentence) {
    return sentence?.humanReadable || sentence?.value;
}

// TestView renders the sentence as elementMap tokens (one hoverable span per
// token), which drop the punctuation and spacing of humanReadable — e.g. the
// comma after a bounded quantifier. Re-align each token against humanReadable
// so the concatenated tokens read as the human-readable sentence while
// keeping the per-token explanations. Returns the original elementMap when
// humanReadable is absent or the tokens can't be aligned to it.
export function buildDisplayElementMap(sentence) {
    const human = sentence?.humanReadable;
    const map = sentence?.elementMap;
    if (!human || !Array.isArray(map) || map.length === 0) {
        return map;
    }

    const pieces = [];
    let pos = 0;
    for (const entry of map) {
        const chars = (entry.element ?? '').replace(/\s+/g, '');
        if (!chars) {
            return map;
        }
        const match = findToken(human, pos, chars);
        if (!match) {
            return map;
        }
        // Text skipped over between tokens (", ", " (", ...): punctuation up
        // to an opening paren trails the previous token, the paren itself
        // and anything after it leads this one.
        const between = human.slice(pos, match.start);
        const parenIdx = between.indexOf('(');
        const trail = (parenIdx === -1 ? between : between.slice(0, parenIdx)).trim();
        const lead = parenIdx === -1 ? '' : between.slice(parenIdx).trimStart();
        if (trail) {
            if (pieces.length === 0) {
                return map;
            }
            pieces[pieces.length - 1].element += trail;
        }
        pieces.push({...entry, element: lead + human.slice(match.start, match.end)});
        pos = match.end;
    }
    const tail = human.slice(pos).trim();
    if (tail) {
        pieces[pieces.length - 1].element += tail;
    }
    return pieces;
}

// Finds the first occurrence of `chars` in `human` at or after `from`,
// allowing whitespace in `human` between the characters (humanReadable may
// space out tokens, e.g. "∀ x" for element "∀x"). Returns {start, end} of
// the matched slice, or null.
function findToken(human, from, chars) {
    for (let start = human.indexOf(chars[0], from); start !== -1; start = human.indexOf(chars[0], start + 1)) {
        let i = start;
        let j = 0;
        while (j < chars.length && i < human.length) {
            if (human[i] === chars[j]) {
                i++;
                j++;
            } else if (/\s/.test(human[i])) {
                i++;
            } else {
                break;
            }
        }
        if (j === chars.length) {
            return {start, end: i};
        }
    }
    return null;
}
